import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';
import * as Dialog from '@radix-ui/react-dialog';
import { useKey, useUnmount } from 'react-use-v13';
import { use100vh } from 'react-div-100vh';
import { SearchResultsList, DebouncedSearchField, QuickGrabMagicSearch } from './components';
import { useMagicSearch, useMagicSearchContext } from './MagicSearchContext';
import { Button, Icon, Text } from '@scale/scaleui-radix';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { RECENT_SEARCHES_SECTION_TITLE } from './constants';

type MagicSearchButtonProps = {
  className?: string;
};

export function MagicSearchButton({ className }: MagicSearchButtonProps) {
  const windowHeight = use100vh();
  const { listRef, searchStore } = useMagicSearchContext();
  const { isOpen, results, focusedListItemIndex } = useMagicSearch(s => ({
    focusedListItemIndex: s.focusedListItemIndex,
    results: s.results,
    isOpen: s.isOpen,
  }));

  const searchScopes = searchStore?.getState().state.searchScopes;

  const resultsSortedByScore = [...results].sort((a, b) => (a.score ?? 1) - (b.score ?? 1));

  const resultsWithoutRecentSearches = resultsSortedByScore.filter(
    result => result.type !== RECENT_SEARCHES_SECTION_TITLE,
  );

  const projectScopeActive = searchScopes?.some((scope: any) => scope.id === 'project');

  const quickGrabContextAvailable = projectScopeActive && resultsWithoutRecentSearches.length === 0;

  useUnmount(() => {
    searchStore?.getState().onResetStore();
  });

  const handleOpenDialogShortCut = useCallback(
    (event: KeyboardEvent) => {
      // Command + K or Window + K
      if (event.metaKey || event.ctrlKey) {
        searchStore?.setState({
          isOpen: !isOpen,
        });
      }
    },
    [isOpen, searchStore],
  );

  useKey('k', handleOpenDialogShortCut);

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={isOpen => {
        searchStore?.setState({
          isOpen,
        });
        if (!isOpen) {
          searchStore?.getState().onResetStore();
        }
      }}
    >
      <Dialog.Trigger asChild>
        <Button
          size="2"
          variant="outline"
          color="neutral"
          className={twMerge(
            'gap-2 text-[#56657D] text-[16px] font-medium font-inter leading-[24px] tracking-[0px] whitespace-nowrap min-w-fit justify-between w-60',
            className,
          )}
        >
          <div className="flex items-center gap-2 justify-start">
            <Icon label="Search" icon={faMagnifyingGlass} size="1" color="neutral" />{' '}
            <Text size="2" color="neutral">
              ⌘ + K to search
            </Text>
          </div>
        </Button>
      </Dialog.Trigger>
      <Dialog.Overlay className="fixed bg-neutral-900/50 inset-0 z-40" />
      <Dialog.Content
        className="fixed z-50 w-full max-w-5xl shadow-md rounded-lg -translate-x-1/2"
        style={{
          top: '5%',
          left: '50%',
          height: windowHeight ? windowHeight - (windowHeight / 100) * 10 : 'calc(100vh - 10%)',
        }}
      >
        <div
          className={
            'flex flex-col bg-neutral-0 focus-within:ring-2 focus-within:ring-primary-400 rounded-lg overflow-hidden duration-300 border border-neutral-200 shadow-md h-full'
          }
        >
          <DebouncedSearchField className="w-full" />
          <SearchResultsList
            className={twMerge(
              'min-h-0 flex-1',
              quickGrabContextAvailable && [
                'flex flex-col h-full',
                resultsSortedByScore.length > 0 ? 'justify-between' : 'justify-end',
              ],
            )}
            ref={listRef}
          >
            <div className="flex flex-col h-full">
              {resultsSortedByScore.map(({ id, Component, props }, index) => (
                <Component
                  key={id}
                  {...props}
                  isFocused={index === focusedListItemIndex}
                  disableHover={focusedListItemIndex !== -1}
                />
              ))}
            </div>
            {quickGrabContextAvailable && <QuickGrabMagicSearch />}
          </SearchResultsList>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
